import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { Button } from "@/components/ui/button";
import Checkbox from "@/components/ui/checkbox";
import Input from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import Text from "@/components/ui/text";
import ConfirmationModal from "@/components/layouts/ConfirmationModal";
import Section from "@/pages/settings/components/sections/Section";
import {
  isHubSpotFormsForbidden,
  isHubSpotNotConnected,
  useHubSpotForms,
  useHubSpotSettings,
  useSaveHubSpotSettings
} from "@/entities/hubspot/hooks";
import {
  HubSpotFormConfig,
  HubSpotFormsSettingsUpdate
} from "@/entities/hubspot/types";
import { useLeadInboundCampaignTypes } from "@/pages/leads/hooks";
import { FormProductMapSection } from "@/pages/settings/components/integrations/hubspot/FormProductMapSection";
import { HubSpotFormDialog } from "@/pages/settings/components/integrations/hubspot/HubSpotFormDialog";
import {
  HUBSPOT_SETTINGS_TRANSLATION_PREFIX as PREFIX,
  MAX_NAME_LENGTH,
  MAX_SELECTED_FORMS,
  blankToNull,
  formRows,
  newFormConfig,
  toFormUpdate,
  toSettingsUpdate
} from "@/pages/settings/components/integrations/hubspot/hubspotSettingsOptions";

interface PropertyInputProps {
  label: string;
  value: string | null;
  required: boolean;
  disabled: boolean;
  onSave: (value: string | null, onError: () => void) => void;
}

// Saves on blur, so typing a name is one write. A blank value is refused while
// a form still reads its product interest from the property, and a refused
// save puts the saved value back.
const PropertyInput: React.FC<PropertyInputProps> = ({
  label,
  value,
  required,
  disabled,
  onSave
}) => {
  const [draft, setDraft] = useState(value ?? "");
  const [syncedValue, setSyncedValue] = useState(value);

  if (value !== syncedValue) {
    setSyncedValue(value);
    setDraft(value ?? "");
  }

  const next = blankToNull(draft);
  const blocked = required && !next;

  return (
    <Input
      data-test="hubspot-settings-productInterestProperty"
      aria-label={label}
      value={draft}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={() => {
        if (!blocked && next !== value) {
          onSave(next, () => setDraft(value ?? ""));
        }
      }}
      maxLength={MAX_NAME_LENGTH}
      hasError={blocked}
      disabled={disabled}
      containerClassName="w-[280px]"
    />
  );
};

export const HubSpotSettings: React.FC = () => {
  const { t } = useTranslation();
  const settingsQuery = useHubSpotSettings();
  const formsQuery = useHubSpotForms();
  const campaignTypesQuery = useLeadInboundCampaignTypes();
  const save = useSaveHubSpotSettings();
  const [editingFormId, setEditingFormId] = useState<string | null>(null);
  const [removingFormId, setRemovingFormId] = useState<string | null>(null);

  const settings = settingsQuery.data;
  const isSaving = save.isPending;
  const controlsDisabled = !settings || isSaving;

  const rows = useMemo(
    () =>
      formRows(
        formsQuery.data ?? [],
        settings?.forms ?? [],
        formsQuery.isSuccess
      ),
    [formsQuery.data, formsQuery.isSuccess, settings?.forms]
  );
  const selectedCount = settings?.forms.length ?? 0;
  const usesProductInterest = Boolean(
    settings?.forms.some((form) => form.productInterest.origin !== "none")
  );
  const editingConfig = settings?.forms.find(
    (form) => form.formId === editingFormId
  );

  const update = (
    changes: (
      current: HubSpotFormsSettingsUpdate
    ) => HubSpotFormsSettingsUpdate,
    onSuccess?: () => void,
    onError?: () => void
  ) => {
    if (!settings) return;
    save.mutate(
      { data: changes(toSettingsUpdate(settings)) },
      {
        onSuccess,
        onError: () => {
          onError?.();
          toast.error(t(`${PREFIX}.updateError`));
        }
      }
    );
  };

  const addForm = (id: string, name: string) =>
    update((current) => ({
      ...current,
      forms: [...current.forms, newFormConfig({ id, name })]
    }));

  // Removing a form discards its configuration, and adding it back starts its
  // intake afresh, so it is confirmed first.
  const removeForm = (id: string) =>
    update(
      (current) => ({
        ...current,
        forms: current.forms.filter((form) => form.formId !== id)
      }),
      () => setRemovingFormId(null)
    );

  const saveForm = (edited: HubSpotFormConfig) => {
    const config = toFormUpdate(edited);
    update(
      (current) => ({
        ...current,
        forms: current.forms.map((form) =>
          form.formId === config.formId ? config : form
        )
      }),
      () => setEditingFormId(null)
    );
  };

  const sectionSubtitle = (key: string) =>
    settingsQuery.isError ? t(`${PREFIX}.loadError`) : t(key);

  // Forms come from HubSpot itself, so they fail on their own.
  const formsSubtitle = () => {
    if (settingsQuery.isError) return t(`${PREFIX}.loadError`);
    if (isHubSpotNotConnected(formsQuery.error)) {
      return t(`${PREFIX}.notConnected`);
    }
    if (isHubSpotFormsForbidden(formsQuery.error)) {
      return t(`${PREFIX}.formsForbidden`);
    }
    if (formsQuery.isError) return t(`${PREFIX}.hubspotLoadError`);
    return t(`${PREFIX}.forms.subtitle`, { max: MAX_SELECTED_FORMS });
  };

  const formSubtitle = (row: (typeof rows)[number]) => {
    if (row.missing) return t(`${PREFIX}.forms.missing`);
    if (!row.config) return "";
    return t(`${PREFIX}.forms.summary`, {
      source: t(`${PREFIX}.form.source.options.${row.config.source}`),
      count: row.config.ownerRules.length
    });
  };

  return (
    <div className="flex w-full flex-col gap-12" data-test="hubspot-settings">
      <Section
        title={t(`${PREFIX}.intake.title`)}
        subTitle={sectionSubtitle(`${PREFIX}.intake.subtitle`)}
        isLoading={settingsQuery.isLoading}
        items={[
          {
            title: t(`${PREFIX}.intake.enabled.title`),
            subTitle: t(`${PREFIX}.intake.enabled.subtitle`),
            customAction: (
              <Switch
                data-test="hubspot-settings-enabled"
                aria-label={t(`${PREFIX}.intake.enabled.title`)}
                checked={settings?.enabled ?? false}
                disabled={controlsDisabled}
                onCheckedChange={(enabled) =>
                  update((current) => ({ ...current, enabled }))
                }
              />
            )
          },
          {
            title: t(`${PREFIX}.intake.productInterestProperty.title`),
            subTitle: t(
              usesProductInterest
                ? `${PREFIX}.intake.productInterestProperty.requiredSubtitle`
                : `${PREFIX}.intake.productInterestProperty.subtitle`
            ),
            customAction: (
              <PropertyInput
                label={t(`${PREFIX}.intake.productInterestProperty.title`)}
                value={settings?.productInterestProperty ?? null}
                required={usesProductInterest}
                disabled={controlsDisabled}
                onSave={(productInterestProperty, onError) =>
                  update(
                    (current) => ({ ...current, productInterestProperty }),
                    undefined,
                    onError
                  )
                }
              />
            )
          }
        ]}
      />
      <Section
        title={t(`${PREFIX}.forms.title`)}
        subTitle={formsSubtitle()}
        isLoading={settingsQuery.isLoading || formsQuery.isLoading}
        items={rows.map((row) => {
          const selected = Boolean(row.config);
          return {
            title: row.name,
            subTitle: formSubtitle(row),
            customAction: (
              <div className="flex items-center gap-3">
                {selected && (
                  <Button
                    variant="secondary"
                    data-test={`hubspot-settings-configure-${row.id}`}
                    onClick={() => setEditingFormId(row.id)}
                    disabled={controlsDisabled}
                  >
                    {t(`${PREFIX}.forms.configure`)}
                  </Button>
                )}
                <Checkbox
                  data-test={`hubspot-settings-form-${row.id}`}
                  aria-label={row.name}
                  checked={selected}
                  disabled={
                    controlsDisabled ||
                    (!selected && selectedCount >= MAX_SELECTED_FORMS)
                  }
                  onCheckedChange={(checked) =>
                    checked === true
                      ? addForm(row.id, row.name)
                      : setRemovingFormId(row.id)
                  }
                />
              </div>
            )
          };
        })}
      >
        {formsQuery.isSuccess && rows.length === 0 && (
          <Text variant="bodySecondary">{t(`${PREFIX}.forms.empty`)}</Text>
        )}
      </Section>
      <FormProductMapSection />
      {editingConfig && (
        <HubSpotFormDialog
          config={editingConfig}
          campaignTypes={campaignTypesQuery.data?.inboundCampaignTypes ?? []}
          isLoadingCampaignTypes={campaignTypesQuery.isLoading}
          isSaving={isSaving}
          hasProductInterestProperty={Boolean(
            settings?.productInterestProperty
          )}
          onSave={saveForm}
          onClose={() => setEditingFormId(null)}
        />
      )}
      <ConfirmationModal
        data-test="hubspot-settings-remove-form"
        isOpen={removingFormId !== null}
        setIsOpen={(open) => {
          if (!open) setRemovingFormId(null);
        }}
        title={t(`${PREFIX}.forms.remove.title`, {
          name: rows.find((row) => row.id === removingFormId)?.name ?? ""
        })}
        confirmationText={t(`${PREFIX}.forms.remove.text`)}
        submitButtonText={t(`${PREFIX}.forms.remove.confirm`)}
        handleSubmit={() => {
          if (removingFormId && !isSaving) removeForm(removingFormId);
        }}
      />
    </div>
  );
};
